export const LOGO_URL =
  'https://cdn.magicpatterns.com/uploads/3dvwyEmrASKZwEK2irUvkH/keystone_technologies_logo_combined.png'

export const IMAGES = {
  hero: 'https://cdn.magicpatterns.com/patterns/generated-images/865a6889-f15d-4775-b459-f9943e08a888.jpg',
  propflow:
    'https://cdn.magicpatterns.com/patterns/generated-images/6fe243d3-9cbf-4826-86d2-fc422a8fcc20.jpg',
  landchain:
    'https://cdn.magicpatterns.com/patterns/generated-images/7d74b88e-0b20-4a34-90ee-9cb03dc04111.jpg',
  sacco:
    'https://cdn.magicpatterns.com/patterns/generated-images/7a8d6561-c6e5-407b-86e1-eaa7e11b8bf6.jpg',
  operations:
    'https://cdn.magicpatterns.com/patterns/generated-images/918c2c25-e4a9-4554-849a-ab1671a3128f.jpg',
}

export const CONTACT_EMAIL = 'info@keystone-technologies.org'

export const CONTACT_PHONES = [
  { display: '+256 702 153 272', tel: '+256702153272' },
  { display: '+256 791 392 193', tel: '+256791392193' },
]

export const MISSION =
  'To turn emerging technologies into practical, trusted tools our clients need to grow and serve their people.'

export const VISION =
  'To give African institutions the ability to operate at scale without losing trust, transparency or control of their own data.'

export const CERTIFICATION = {
  name: 'Data Protection Office',
  label: 'Certified by the Data Protection Office',
  text: 'Keystone Technologies is certified by the Data Protection Office. Personal data held in our products — tenants, members, landholders — is processed lawfully, stored securely, and stays under the control of the institution that owns it.',
}

export const FOOTPRINT = [
  { market: 'Kigali', country: 'Rwanda', properties: 5, units: 100 },
  { market: 'Uganda', country: 'Kampala and surrounds', properties: 4, units: 31 },
]

export const FOOTPRINT_TOTALS = FOOTPRINT.reduce(
  (acc, row) => ({
    properties: acc.properties + row.properties,
    units: acc.units + row.units,
  }),
  { properties: 0, units: 0 },
)

export const NAV_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Products', to: '/products' },
  { label: 'PropFlow', to: '/propflow' },
  { label: 'About', to: '/about' },
  { label: 'Contact', to: '/contact' },
]

export type ProductStatus = 'Live' | 'In development' | 'Coming soon'

export type Product = {
  slug: string
  name: string
  category: string
  status: ProductStatus
  timing: string
  image: string
  lede: string
  body: string
  points: string[]
  to?: string
}

export const PRODUCTS: Product[] = [
  {
    slug: 'propflow',
    name: 'PropFlow',
    category: 'Property Management System',
    status: 'Live',
    timing: 'Available now',
    image: IMAGES.propflow,
    lede: 'One platform for every unit, tenant, payment, and repair — with AI workflows that chase the routine work for you.',
    body: 'Landlords and property managers across Africa run portfolios on WhatsApp threads, paper receipts, and memory. PropFlow puts the whole portfolio in one place and automates the follow-up: rent reminders go out on schedule, arrears escalate on their own, and maintenance requests move from tenant message to assigned technician without anyone re-typing them.',
    points: [
      'Automated rent reminders across SMS, WhatsApp, and email',
      'Arrears alerts that escalate before a month is lost',
      'Maintenance requests tracked from report to sign-off',
      'Mobile money and bank payments reconciled automatically',
    ],
    to: '/propflow',
  },
  {
    slug: 'landchain',
    name: 'LandChain',
    category: 'Blockchain Land Information System',
    status: 'In development',
    timing: 'Pilot 2026',
    image: IMAGES.landchain,
    lede: 'A tamper-evident register of land records, built to make fraudulent titles impossible to hide.',
    body: 'Land fraud across the continent thrives on paper registries that can be altered, duplicated, or lost. LandChain writes every transaction — survey, transfer, caveat, mortgage — to an append-only ledger where the full history of a parcel is verifiable by anyone with a right to see it, while the registry stays under the authority that owns it.',
    points: [
      'Append-only parcel history no single office can rewrite',
      'Instant title verification for buyers, banks, and courts',
      'Digitization pathway for existing paper registries',
      'Designed to sit under national and district land authorities',
    ],
  },
  {
    slug: 'sacco-suite',
    name: 'SACCO Suite',
    category: 'Microfinance & SACCO Management',
    status: 'Coming soon',
    timing: 'Launching 2026',
    image: IMAGES.sacco,
    lede: 'Core banking for savings groups and microfinance institutions that have outgrown the ledger book.',
    body: 'SACCOs and microfinance institutions carry real financial weight in African communities and almost none of the software. SACCO Suite handles members, shares, savings, loans, and repayments with the audit trail regulators expect — and the mobile money integration members already use every day.',
    points: [
      'Member, share, and savings accounts in one register',
      'Loan origination, scheduling, and repayment tracking',
      'Mobile money collections and disbursements',
      'Regulator-ready statements and audit trails',
    ],
  },
]

export const PROPFLOW_WORKFLOWS = [
  {
    name: 'Rent reminders',
    trigger: 'Seven days before due date',
    text: 'Tenants receive a reminder on the channel they actually read, in the language they use, with the exact balance and payment reference. Follow-ups continue until payment clears.',
  },
  {
    name: 'Arrears escalation',
    trigger: 'Day after a missed payment',
    text: 'The account is flagged, the tenant gets a graduated notice, and the manager sees the exposure on the dashboard — before a single missed month quietly becomes three.',
  },
  {
    name: 'Maintenance intake',
    trigger: 'When a tenant reports an issue',
    text: 'A request arriving by WhatsApp or the tenant portal becomes a structured ticket with photos, unit, and urgency, routed to the right technician with the landlord copied in.',
  },
  {
    name: 'Lease and inspection alerts',
    trigger: 'Sixty days before expiry',
    text: 'Renewals, inspections, and compliance dates surface early enough to act on, so nothing lapses because it was only ever in someone’s head.',
  },
]

export const PROPFLOW_MODULES = [
  {
    title: 'Portfolio overview',
    text: 'Every property, unit, and occupancy status on one screen — across multiple landlords if you manage on behalf of owners.',
  },
  {
    title: 'Tenants and leases',
    text: 'Documents, deposits, lease terms, and communication history attached to the tenant, not to a phone.',
  },
  {
    title: 'Payments and reconciliation',
    text: 'Mobile money, bank transfer, and cash receipts matched to invoices automatically, with a clear arrears position.',
  },
  {
    title: 'Maintenance and vendors',
    text: 'Ticket queue, technician assignment, cost tracking, and a record of what was fixed and when.',
  },
  {
    title: 'Owner reporting',
    text: 'Statements and remittance summaries generated per owner, ready to send without rebuilding a spreadsheet.',
  },
  {
    title: 'Roles and access',
    text: 'Caretakers, agents, accountants, and owners each see exactly what their role requires — nothing more.',
  },
]

export const PROPFLOW_METRICS = [
  { value: '4 hrs', label: 'Admin time saved per property, per week' },
  { value: '0', label: 'Requests lost between WhatsApp and the technician' },
  { value: '1', label: 'Platform instead of six spreadsheets' },
]

export const STATS = [
  { value: '131', label: 'Units managed on PropFlow' },
  { value: '9', label: 'Properties across two markets' },
  { value: 'Kigali · Uganda', label: 'Live today' },
  { value: 'DPO certified', label: 'Data Protection Office' },
]

export const PRINCIPLES = [
  {
    title: 'Built for how Africa actually works',
    text: 'Mobile money before card rails. WhatsApp before email. Intermittent connectivity assumed, not treated as an edge case. Our products are designed around the conditions our users operate in, not ported from elsewhere and patched.',
  },
  {
    title: 'Nothing falls through',
    text: 'Every product we build exists to stop information disappearing — a missed rent notice, an altered title, an unrecorded repayment. Automation is not a feature here; it is the reason the system is trustworthy.',
  },
  {
    title: 'One platform, not six tools',
    text: 'Operators should not have to reconcile their own business across apps. We would rather ship one system that holds the whole picture than a suite that fragments it.',
  },
]

export const APPROACH = [
  {
    step: 'Sit with the operator',
    weeks: 'Discovery',
    text: 'We start in the office of the person doing the work — the property manager, the registrar, the SACCO clerk — and map the process as it truly runs, receipts and all.',
  },
  {
    step: 'Automate the follow-up',
    weeks: 'Design',
    text: 'The repetitive chasing is where information is lost. We design the workflow that removes it first, then build the record-keeping around it.',
  },
  {
    step: 'Launch with one operator',
    weeks: 'Pilot',
    text: 'Each product goes live with real portfolios and real money before it is offered widely. Nothing ships on the strength of a demo.',
  },
  {
    step: 'Scale across the market',
    weeks: 'Rollout',
    text: 'Once the workflow holds under real conditions, we extend to new markets and adapt for local payment rails, languages, and regulation.',
  },
]

export const LEADERSHIP = [
  {
    name: 'Founder & CEO',
    role: 'Product and market',
    bio: 'Leads product direction and works directly with the landlords, registrars, and SACCO managers our systems are built for.',
  },
  {
    name: 'Chief Technology Officer',
    role: 'Engineering and architecture',
    bio: 'Owns platform architecture across PropFlow, LandChain, and SACCO Suite, including the automation and ledger layers.',
  },
  {
    name: 'Head of Operations',
    role: 'Delivery and support',
    bio: 'Runs onboarding, training, and support so operators are productive in days rather than quarters.',
  },
]

export const OFFICES = [
  { city: 'Kigali', line1: '5 properties · 100 units', line2: 'Kigali, Rwanda' },
  { city: 'Uganda', line1: '4 properties · 31 units', line2: 'Kampala, Uganda' },
]

export const FAQS = [
  {
    q: 'Do my tenants need to download an app?',
    a: 'No. Reminders and maintenance requests work over SMS and WhatsApp, which tenants already use. A tenant portal is available for those who want it.',
  },
  {
    q: 'Which payment methods are supported?',
    a: 'Mobile money, bank transfer, and cash receipting. Payments are matched to invoices automatically so your arrears position is always current.',
  },
  {
    q: 'Can I manage properties for multiple owners?',
    a: 'Yes. Agencies can separate portfolios by owner, control who sees what, and generate per-owner statements and remittance summaries.',
  },
  {
    q: 'What happens to my existing records?',
    a: 'We migrate your properties, units, tenants, and outstanding balances during onboarding, usually from spreadsheets or a paper file.',
  },
]
